import { HistoryOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { BEHAVIOUR_LABELS } from '../../../common/constants';
import CommonTable from '../../../components/CommonTable';

const ORANGE_BEHAVIOURS = ['wfh_over', 'hours_over'];

/**
 * Returns the incident summary object (rawCount, excusedCount) for a given
 * behaviour from the employee's incidents array.
 */
function getIncident(incidents, behaviour) {
  return incidents?.find((i) => i.behaviourKey === behaviour) || null;
}

/**
 * Renders a clickable badge for an incident cell.
 * Strikethrough grey when fully genuine.
 */
function IncidentBadge({ incident, behaviour, onExcuse, canUpdate, clickable }) {
  if (!incident || incident.rawCount === 0) {
    return <span style={{ color: '#bfbfbf' }}>—</span>;
  }

  const { rawCount, excusedCount } = incident;
  const effectiveCount = Math.max(0, rawCount - excusedCount);
  const isFullyExcused = effectiveCount === 0 && excusedCount > 0;
  const isOrange = ORANGE_BEHAVIOURS.includes(behaviour);
  const isClickable = canUpdate && clickable;

  let badgeClass = 'default-badge';
  if (isFullyExcused) {
    badgeClass = 'excused-badge';
  } else if (isOrange) {
    badgeClass = 'orange-badge';
  }

  const tooltipTitle = isFullyExcused
    ? `${BEHAVIOUR_LABELS[behaviour]}: ${rawCount} raw (fully genuine)`
    : `${BEHAVIOUR_LABELS[behaviour]}: ${rawCount} raw, ${excusedCount} genuine`;

  return (
    <Tooltip title={tooltipTitle}>
      <span
        className={`at-count-badge ${badgeClass}`}
        style={isClickable ? undefined : { cursor: 'default' }}
        onClick={() => isClickable && onExcuse && onExcuse(incident, behaviour)}
      >
        <span className="at-count-value">{rawCount}</span>
        {excusedCount > 0 && !isFullyExcused && (
          <span className="at-excused-note">{`(${excusedCount} genuine)`}</span>
        )}
      </span>
    </Tooltip>
  );
}

/**
 * Score badge with colour based on score range. Clickable to open the
 * score breakdown modal when onClick is provided.
 */
function ScoreBadge({ score, onClick }) {
  if (score === null || score === undefined) {
    return <span style={{ color: '#bfbfbf' }}>N/A</span>;
  }

  let cls = 'score-red';
  if (score >= 8.0) cls = 'score-green';
  else if (score >= 6.0) cls = 'score-blue';
  else if (score >= 4.0) cls = 'score-orange';

  return (
    <Tooltip title={`Score: ${score.toFixed(1)} — click for breakdown`}>
      <span
        className={`at-score-badge ${cls}`}
        style={onClick ? { cursor: 'pointer' } : undefined}
        onClick={onClick}
      >
        {score.toFixed(1)}
      </span>
    </Tooltip>
  );
}

/**
 * AttendanceTrackerView renders the Ant Design table with one row per employee.
 */
function formatPeriod(view, record) {
  if (view === 'MONTH') {
    return dayjs(`${record.year}-${record.month}-01`).format('MMMM YYYY');
  }
  if (view === 'QUARTER') {
    return `Q${record.quarter} ${record.year}`;
  }
  return `${record.year}`;
}

function AttendanceTrackerView({
  data,
  loading,
  view,
  onCountClick,
  onChange,
  onScoreClick,
  onViewHistory,
  paginationConfig,
  canUpdate,
}) {
  const BEHAVIOUR_COLUMNS = [
    { key: 'leave', behaviourKey: 'leave', title: 'Unplanned Leave' },
    { key: 'wfhOver', behaviourKey: 'wfh_over', title: 'WFH Over Allowance' },
    {
      key: 'hoursOver',
      behaviourKey: 'hours_over',
      title: 'Hours Adjustment Over Allowance',
    },
    { key: 'late', behaviourKey: 'late', title: 'Coming Late' },
    { key: 'early', behaviourKey: 'early', title: 'Leaving Early' },
    { key: 'standup', behaviourKey: 'standup', title: 'Missed Standup' },
    { key: 'xbrk', behaviourKey: 'xbrk', title: 'Extended Break' },
    { key: 'sbrk', behaviourKey: 'sbrk', title: 'Short Break' },
  ];

  const columns = [
    {
      title: 'Employee',
      key: 'employee',
      fixed: 'left',
      width: 200,
      render: (_, record) => (
        <div className="at-employee-cell">
          <span>
            {`${record.employee?.firstName} ${record.employee?.lastName}`}
          </span>
          {record.employee?.level && (
            <span className="at-level-chip">{record.employee.level.name}</span>
          )}
        </div>
      ),
    },
    ...(view === 'YEAR'
      ? []
      : [
          {
            title: 'Period',
            key: 'period',
            width: 130,
            align: 'center',
            render: (_, record) => formatPeriod(view, record),
          },
        ]),
    ...BEHAVIOUR_COLUMNS.map(({ key, behaviourKey, title }) => ({
      title,
      key,
      align: 'center',
      width: 130,
      render: (_, record) => {
        const behaviourData = record[key] || {};
        const rawCount = behaviourData.rawCount ?? 0;
        const matchedIncident = getIncident(record.incidents, behaviourKey);
        const incident = rawCount
          ? {
              id: matchedIncident?.id,
              rawCount,
              excusedCount: behaviourData.excusedCount ?? 0,
              effectiveCount: behaviourData.effectiveCount ?? rawCount,
            }
          : null;
        return (
          <div className="at-incident-cell">
            <IncidentBadge
              incident={incident}
              behaviour={behaviourKey}
              canUpdate={canUpdate}
              clickable={view === 'QUARTER' || view === 'YEAR'}
              onExcuse={(inc, beh) =>
                onCountClick &&
                onCountClick(record, inc, {
                  fieldKey: key,
                  behaviourKey: beh,
                  title,
                })
              }
            />
          </div>
        );
      },
    })),
    {
      title: 'Score',
      key: 'score',
      align: 'center',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <div className="d-flex justify-center gap-8">
          <ScoreBadge
            score={record.score}
            onClick={onScoreClick ? () => onScoreClick(record) : undefined}
          />
          {onViewHistory && (
            <Tooltip title="View incident history">
              <HistoryOutlined
                className="at-history-icon"
                onClick={() => onViewHistory(record)}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <CommonTable
      size="small"
      bordered
      columns={columns}
      data={data || []}
      loadingData={loading}
      rowKey={(obj) => obj?.employeeId}
      scroll={{ x: 1400, y: '100%' }}
      onChange={onChange}
      paginationConfig={paginationConfig}
    />
  );
}

export default AttendanceTrackerView;
